import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, TextInput, Platform, Pressable, ActivityIndicator } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SelectInput from '@/components/Inputs/SelectInput';
import SwitchInput from '@/components/Inputs/Switch';
import { filesApi, toolsApi } from '@/lib/api';
import { useToastStore, useAuthStore } from '@/store';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';

// Match `Text.jsx`'s font selection so native HTML inputs (date/time/color on
// web) and RN TextInputs both render with the app's typography. The web string
// is a CSS font stack; native gets the system family.
const APP_FONT_FAMILY = Platform.select({
  ios: 'System',
  android: 'Roboto',
  default: DEFAULT_FONT_FAMILY,
});
import MultiImageUpload from '@/components/Tools/AdvancedComponents/MultiImageUpload';
import ImageMaskEditor from '@/components/Tools/AdvancedComponents/ImageMaskEditor';
import VideoTrimmer from '@/components/Tools/AdvancedComponents/VideoTrimmer';
import AudioWaveform from '@/components/Tools/AdvancedComponents/AudioWaveform';
import PipelineProgress from '@/components/Tools/AdvancedComponents/PipelineProgress';
import Dropzone from '@/components/Tools/AdvancedComponents/Dropzone';
import FilePreviewTile from '@/components/Tools/AdvancedComponents/FilePreviewTile';
import { LinearGradient } from 'expo-linear-gradient';
import {
  describeFileLimitViolation,
  formatFileUploadLimits,
  getFileValidationLimits,
  probeMediaDurationSeconds,
} from '@/constants/utils/file_upload_limits';

// Lazily required so non-web bundles don't import the native module unless used.
// All five date/time/color controls live in this file (small, focused) instead
// of dedicated components — they share a common visual language with the rest
// of the form (border, padding, theming) and have no reusable consumers yet.
const NativeDateTimePicker = Platform.OS !== 'web'
  ? require('@react-native-community/datetimepicker').default
  : null;

// Resolve a "model-bound" field into an effective field.
//
// When `field.bindToModelParameter` is set, the field's option list, default
// value, and numeric/string constraints come from the *currently selected
// model's* `parameters[]` entry — not from the tool seed. This is what lets a
// single tool (e.g. AI Text to Speech) work across providers with different
// voice catalogs without forking the tool per model.
//
// The returned field is shallow-merged so anything the tool seed declared
// (label, description, ui.component, etc.) is preserved; only the
// model-specific bits get overridden.
function fileTypeFromField(field) {
  const fileComponents = ['file', 'image', 'imageMask', 'imageMultiple', 'multiImageUpload', 'fileMultiple', 'audio', 'audioWaveform', 'video', 'videoTrimmer', 'pointCloud'];
  if (field?.type !== 'file' && !fileComponents.includes(field?.ui?.component)) return null;
  const fromVal = field?.validation?.file?.fileType;
  if (fromVal) return fromVal;
  const component = field?.ui?.component;
  if (component === 'audio' || component === 'audioWaveform') return 'audio';
  if (component === 'video' || component === 'videoTrimmer') return 'video';
  if (
    component === 'image'
    || component === 'imageMask'
    || component === 'imageMultiple'
    || component === 'multiImageUpload'
  ) return 'image';
  return null;
}

function mergeModelFileValidation(field, modelParam) {
  const modelFile = modelParam?.validation?.file;
  if (!modelFile) return field;
  return {
    ...field,
    validation: {
      ...(field.validation || {}),
      file: {
        ...(field.validation?.file || {}),
        ...modelFile,
        accept: field.validation?.file?.accept || modelFile.accept,
      },
    },
  };
}

export function resolveBoundField(field, selectedModel) {
  if (!field) return field;

  const params = selectedModel?.parameters || [];
  const paramName = field?.bindToModelParameter;
  let modelParam = null;

  if (paramName) {
    modelParam = params.find(p => p?.name === paramName);
    // Video duration: Kling models use `duration` while Veo/Wan use `seconds`.
    if (!modelParam && paramName === 'seconds') {
      modelParam = params.find(p => p?.name === 'duration');
    } else if (!modelParam && paramName === 'duration') {
      modelParam = params.find(p => p?.name === 'seconds');
    }
  }

  if (modelParam) {
    const modelOptions = modelParam.validation?.options || [];
    // Prefer the tool's configured default when it is still a valid choice for
    // this model (e.g. image-editor defaults to "auto" / match-source even when
    // the model parameter's own default is 1:1).
    const toolDefault = field.value;
    const toolDefaultAllowed = toolDefault != null && toolDefault !== '' && (
      !modelOptions.length
      || modelOptions.some((o) => String(o?.value) === String(toolDefault))
    );

    const merged = {
      ...field,
      value: toolDefaultAllowed ? toolDefault : (modelParam.value ?? field.value),
      validation: {
        ...(field.validation || {}),
        // Pull constraint blocks straight from the model — these are the
        // ones a user-facing select / slider / number actually reads.
        options: modelOptions.length ? modelOptions : (field.validation?.options || []),
        ...(modelParam.validation?.number ? { number: modelParam.validation.number } : {}),
        ...(modelParam.validation?.string ? { string: modelParam.validation.string } : {}),
      },
    };
    return mergeModelFileValidation(merged, modelParam);
  }

  // File fields often aren't `bindToModelParameter`'d (the processor maps
  // them onto `message.files`). Still overlay the selected model's matching
  // file param so size/duration caps follow the picker.
  const fileType = fileTypeFromField(field);
  if (fileType) {
    const fileParam = params.find((p) => (
      p?.type === 'file' && p?.validation?.file?.fileType === fileType
    ));
    if (fileParam) return mergeModelFileValidation(field, fileParam);
  }

  return field;
}

/**
 * Dynamic renderer for a single tool form field.
 *
 * Maps a Tool.fields[] entry (see backend/utils/custom_param_schema.js) to a
 * concrete input. Falls back to a single-line text field when the requested
 * component isn't yet implemented in the MVP renderer.
 *
 * `selectedModel` (optional) is the currently-resolved Model document for the
 * tool run. When a field declares `bindToModelParameter`, options and default
 * value are resolved from that model's `parameters[]` entry instead of the
 * tool seed — see `resolveBoundField` above.
 */
function ToolFormField({ field: rawField, value, onChange, disabled, error, toolSlug, contextValues, selectedModel, minimal = false }) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);

  const field = useMemo(
    () => resolveBoundField(rawField, selectedModel),
    [rawField, selectedModel],
  );

  const fileLimits = useMemo(
    () => getFileValidationLimits(field?.validation?.file),
    [field],
  );
  const fileLimitsHint = useMemo(
    () => formatFileUploadLimits(fileLimits),
    [fileLimits],
  );

  const styles = useMemo(() => StyleSheet.create({
    container: {
      width: '100%',
      marginBottom: minimal ? 20 : 16,
    },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
      gap: 4,
    },
    description: {
      marginBottom: 8,
      lineHeight: 18,
    },
    input: {
      width: '100%',
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: minimal ? 12 : 8,
      backgroundColor: minimal ? palette.control : colors.solid_color_tertiary,
      color: colors.text_color_primary,
      fontSize: 15,
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    textarea: {
      minHeight: minimal ? 120 : 100,
      lineHeight: 22,
      paddingTop: 10,
      textAlignVertical: 'top',
    },
    toggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 4,
    },
    error: {
      marginTop: 4,
    },
  }), [colors, error, minimal, palette]);

  const ui = field.ui || {};
  const validation = field.validation || {};
  const component = ui.component || inferComponentFromType(field);
  const label = minimal ? (ui.displayName || field.name).replace(/\s*\(optional\)/i, '') : (ui.displayName || field.name);
  const placeholder = ui.placeholder || '';
  const isRequired = !!validation.required;
  const description = ui.description || field.description;

  const renderLabel = () => (
    <View style={styles.labelRow}>
      <Text size={minimal ? 's' : 'n'} weight='semibold' color='primary'>{label}</Text>
      {isRequired && <Text size='n' color='danger' weight='bold'>*</Text>}
    </View>
  );

  const renderError = () => (
    error ? <Text color='danger' size='s' style={styles.error}>{error}</Text> : null
  );

  const renderDescription = () => (
    <>
      {description && !(minimal && placeholder && ['textarea', 'text'].includes(component)) ? (
        <Text size='s' color='secondary' style={styles.description}>{description}</Text>
      ) : null}
      {!minimal && fileLimitsHint && fileTypeFromField(field) ? (
        <Text size='s' color='tertiary' style={[styles.description, description ? { marginTop: -4 } : null]}>
          {fileLimitsHint}
        </Text>
      ) : null}
    </>
  );

  const sharedTextProps = {
    placeholder,
    accessibilityLabel: label,
    placeholderTextColor: colors.color_input_placeholder,
    editable: !disabled,
    value: value == null ? '' : String(value),
    onChangeText: onChange,
  };

  const aiAssist = !!ui.aiAssist;

  switch (component) {
    case 'textarea': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <View style={{ position: 'relative', width: '100%' }}>
            <TextInput
              {...sharedTextProps}
              multiline
              style={[
                styles.input,
                styles.textarea,
                aiAssist && { paddingRight: 44 },
              ]}
              maxLength={validation.string?.maxLength}
            />
            {aiAssist && (
              <MagicEnhanceButton
                toolSlug={toolSlug}
                fieldName={field.name}
                currentValue={value}
                contextValues={contextValues}
                disabled={disabled}
                onApply={onChange}
              />
            )}
          </View>
          {renderError()}
        </View>
      );
    }

    case 'number': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <NumberControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            min={validation?.number?.min}
            max={validation?.number?.max}
            step={validation?.number?.step}
            error={error}
          />
          {renderError()}
        </View>
      );
    }

    case 'slider': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <SliderControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            min={validation?.number?.min ?? 0}
            max={validation?.number?.max ?? 100}
            step={validation?.number?.step ?? 1}
            error={error}
          />
          {renderError()}
        </View>
      );
    }

    case 'image':
    case 'file': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <FileFieldControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || (component === 'image' ? 'image/*' : undefined)}
            label={ui?.uploadLabel}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
            maxDurationSeconds={fileLimits.maxDurationSeconds}
          />
          {renderError()}
        </View>
      );
    }

    case 'fileMultiple':
    case 'imageMultiple':
    case 'multiImageUpload':
    case 'layerViewer': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <MultiImageUpload
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || (component === 'fileMultiple' ? '*/*' : 'image/*')}
            label={ui?.uploadLabel || 'Add files'}
            slots={ui?.slots || []}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
            maxDurationSeconds={fileLimits.maxDurationSeconds}
          />
          {renderError()}
        </View>
      );
    }

    case 'imageMask': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <ImageMaskEditor
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || 'image/*'}
            label={ui?.uploadLabel || 'Choose image'}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
          />
          {renderError()}
        </View>
      );
    }

    case 'audio':
    case 'audioWaveform': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <AudioWaveform
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || 'audio/*'}
            label={ui?.uploadLabel || 'Choose audio'}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
            maxDurationSeconds={fileLimits.maxDurationSeconds}
          />
          {renderError()}
        </View>
      );
    }

    case 'video':
    case 'videoTrimmer': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <VideoTrimmer
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || 'video/*'}
            label={ui?.uploadLabel || 'Choose video'}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
            maxDurationSeconds={fileLimits.maxDurationSeconds}
          />
          {renderError()}
        </View>
      );
    }

    case 'pointCloud': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <FileFieldControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            accept={validation?.file?.accept || ui?.accept || '.ply,.pcd,.xyz,.las,.laz'}
            label={ui?.uploadLabel || 'Choose point cloud'}
            limitsHint={fileLimitsHint}
            maxSizeBytes={fileLimits.maxSizeBytes}
            maxDurationSeconds={fileLimits.maxDurationSeconds}
          />
          {renderError()}
        </View>
      );
    }

    case 'pipelineProgress': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <PipelineProgress stages={ui?.stages || []} status='configured' />
          {renderError()}
        </View>
      );
    }

    case 'color': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <ColorControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder || '#000000'}
          />
          {renderError()}
        </View>
      );
    }

    case 'date': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <DateControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder || 'YYYY-MM-DD'}
          />
          {renderError()}
        </View>
      );
    }

    case 'daterange': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <DateRangeFieldControl
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
          {renderError()}
        </View>
      );
    }

    case 'time': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <TimeControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder || 'HH:MM'}
          />
          {renderError()}
        </View>
      );
    }

    case 'datetime': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <DateTimeControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder || 'YYYY-MM-DD HH:MM'}
          />
          {renderError()}
        </View>
      );
    }

    case 'url': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <UrlControl
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={validation.string?.maxLength}
            error={error}
          />
          {renderError()}
        </View>
      );
    }

    case 'text': {
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <View style={{ position: 'relative', width: '100%' }}>
            <TextInput
              {...sharedTextProps}
              autoCapitalize='none'
              autoCorrect
              style={[styles.input, aiAssist && { paddingRight: 44 }]}
              maxLength={validation.string?.maxLength}
            />
            {aiAssist && (
              <MagicEnhanceButton
                toolSlug={toolSlug}
                fieldName={field.name}
                currentValue={value}
                contextValues={contextValues}
                disabled={disabled}
                onApply={onChange}
              />
            )}
          </View>
          {renderError()}
        </View>
      );
    }

    case 'select': {
      const items = (validation.options || []).map(opt => {
        const baseLabel = opt.label || String(opt.value);
        // When an option carries a one-liner description (e.g. model
        // parameter catalogs do — "neutral, balanced" for a TTS voice),
        // splice it into the label as "Name — description". Keeps the
        // dropdown self-explanatory without needing a richer SelectInput.
        const label = opt.description ? `${baseLabel} — ${opt.description}` : baseLabel;
        return { label, value: opt.value };
      });
      return (
        <View style={styles.container}>
          <SelectInput
            label={label}
            isRequired={isRequired}
            subtitle={description}
            value={value ?? field.value ?? validation.default}
            onValueChange={onChange}
            items={items}
            isDisabled={disabled}
            placeholder={placeholder ? { label: placeholder, value: null } : undefined}
            error={error}
          />
        </View>
      );
    }

    case 'multiselect': {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const opts = validation.options || [];
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <MultiSelectControl
            value={arr}
            onChange={onChange}
            options={opts}
            disabled={disabled}
            placeholder={placeholder}
            maxItems={validation.array?.maxItems}
            error={error}
          />
          {renderError()}
        </View>
      );
    }

    case 'toggle': {
      return (
        <View style={styles.container}>
          <View style={styles.toggleRow}>
            <View style={{ flex: 1, marginRight: 12 }}>
              {renderLabel()}
              {renderDescription()}
            </View>
            <SwitchInput value={!!value} onValueChange={onChange} />
          </View>
          {renderError()}
        </View>
      );
    }

    // Fallback: text. Files & richer inputs are tracked for a follow-up MVP.
    default:
      return (
        <View style={styles.container}>
          {renderLabel()}
          {renderDescription()}
          <TextInput
            {...sharedTextProps}
            style={styles.input}
          />
          {renderError()}
        </View>
      );
  }
}

function inferComponentFromType(field) {
  if (Array.isArray(field?.validation?.options) && field.validation.options.length) return 'select';
  switch (field?.type) {
    case 'boolean': return 'toggle';
    case 'number':  return 'number';
    case 'array':   return 'multiselect';
    case 'file':    return 'file';
    default:        return 'text';
  }
}

// ---------------------------------------------------------------------------
// MagicEnhanceButton — small wand pill anchored top-right of the input.
//
// Calls toolsApi.enhanceField with the field name, current draft, and the rest
// of the form values (so the model can respect tone/genre/length etc), then
// replaces the field value with the suggestion. Empty drafts get a fresh
// creative example; non-empty drafts get an in-place rewrite/enhancement.
// ---------------------------------------------------------------------------
function MagicEnhanceButton({ toolSlug, fieldName, currentValue, contextValues, disabled, onApply }) {
  const addToast = useToastStore(s => s.actions.addToast);
  const token = useAuthStore(s => s.token);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handlePress = useCallback(async () => {
    if (loading || disabled || !toolSlug || !fieldName) return;
    if (!token) {
      addToast?.('Sign in to use AI suggestions', 'warning');
      return;
    }
    setLoading(true);
    try {
      const res = await toolsApi.enhanceField(toolSlug, {
        fieldName,
        currentValue: currentValue == null ? '' : String(currentValue),
        contextValues: contextValues || {},
      });
      if (res?.error) {
        throw new Error(res?.message || 'Could not generate a suggestion');
      }
      const enhanced = typeof res?.enhanced === 'string' ? res.enhanced.trim() : '';
      if (enhanced) {
        onApply?.(enhanced);
      } else {
        addToast?.(res?.message || 'Could not generate a suggestion', 'warning');
      }
    } catch (e) {
      addToast?.(e?.message || 'Could not generate a suggestion', 'warning');
    } finally {
      setLoading(false);
    }
  }, [loading, disabled, toolSlug, fieldName, currentValue, contextValues, onApply, addToast, token]);

  const isEmpty = !currentValue || (typeof currentValue === 'string' && !currentValue.trim());
  const tooltip = isEmpty ? 'Generate a prompt' : 'Improve this prompt';

  return (
    <Pressable
      onPress={handlePress}
      disabled={loading || disabled}
      onHoverIn={() => Platform.OS === 'web' && setHovered(true)}
      onHoverOut={() => Platform.OS === 'web' && setHovered(false)}
      accessibilityRole='button'
      accessibilityLabel={tooltip}
      {...(Platform.OS === 'web' ? { title: tooltip } : null)}
      style={({ pressed }) => ([
        {
          position: 'absolute',
          top: 6,
          right: 6,
          width: 32,
          height: 32,
          borderRadius: 8,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: hovered ? '#7c3aed' : 'rgba(124, 58, 237, 0.10)',
          borderWidth: 1,
          borderColor: hovered ? '#7c3aed' : 'rgba(124, 58, 237, 0.35)',
          opacity: (loading || disabled) ? 0.65 : 1,
          ...(Platform.OS === 'web' ? { transition: 'background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease', cursor: (loading || disabled) ? 'not-allowed' : 'pointer' } : null),
          ...(pressed ? { transform: [{ scale: 0.96 }] } : null),
        },
      ])}
    >
      {loading ? (
        <ActivityIndicator size='small' color={hovered ? '#fff' : '#7c3aed'} />
      ) : (
        <Icon
          name='wand-magic-sparkles'
          size={14}
          color={hovered ? '#fff' : '#7c3aed'}
        />
      )}
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// MultiSelectControl — proper chip-based multi-select.
//
// Two modes:
//   • Options-bound: render each option as a togglable chip. Selected chips
//     fill in with the primary color, unselected chips render outlined. The
//     full set is always visible so users can scan available choices.
//   • Free-form: render the same chip strip for already-selected values plus
//     a small text input that adds chips on Enter/comma and supports
//     backspace-to-remove-last-chip.
//
// Shared:
//   - Respects `maxItems` (validation.array.maxItems) and quietly stops adding.
//   - Emits a string[] in either mode for parity with the backend's array
//     validators.
// ---------------------------------------------------------------------------
function MultiSelectControl({ value, onChange, options, disabled, placeholder, maxItems, error }) {
  const { colors } = useTheme();
  const selected = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const hasOptions = Array.isArray(options) && options.length > 0;
  const [draft, setDraft] = useState('');

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
      borderRadius: 10,
      paddingHorizontal: 10,
      paddingVertical: 8,
      minHeight: 44,
      alignItems: 'center',
    },
    chip: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      paddingHorizontal: 10, paddingVertical: 6,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    chipSelected: {
      backgroundColor: colors.text_color_primary,
      borderColor: colors.text_color_primary,
    },
    chipDisabled: { opacity: 0.5 },
    chipLabelOnSelected: { color: colors.solid_color_primary },
    addInput: {
      flexGrow: 1,
      minWidth: 100,
      paddingVertical: 6,
      paddingHorizontal: 4,
      color: colors.text_color_primary,
      fontSize: 14,
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    footerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 6,
    },
  }), [colors, error]);

  const isAtCap = maxItems != null && selected.length >= maxItems;

  const toggleOption = useCallback((opt) => {
    if (disabled) return;
    const present = selected.includes(opt.value);
    if (present) {
      onChange?.(selected.filter(v => v !== opt.value));
    } else {
      if (isAtCap) return;
      onChange?.([...selected, opt.value]);
    }
  }, [disabled, isAtCap, onChange, selected]);

  const removeChip = useCallback((v) => {
    if (disabled) return;
    onChange?.(selected.filter(x => x !== v));
  }, [disabled, onChange, selected]);

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim().replace(/,+$/, '');
    if (!trimmed) return;
    if (selected.includes(trimmed)) { setDraft(''); return; }
    if (isAtCap) { setDraft(''); return; }
    onChange?.([...selected, trimmed]);
    setDraft('');
  }, [draft, isAtCap, onChange, selected]);

  // -- Options-bound: show all options as toggleable chips ------------------
  if (hasOptions) {
    return (
      <View>
        <View style={styles.wrap}>
          {options.map((opt) => {
            const isSelected = selected.includes(opt.value);
            const optionDisabled = disabled || (!isSelected && isAtCap);
            return (
              <Pressable
                key={String(opt.value)}
                onPress={() => toggleOption(opt)}
                disabled={optionDisabled}
                style={[
                  styles.chip,
                  isSelected && styles.chipSelected,
                  optionDisabled && styles.chipDisabled,
                ]}
              >
                {isSelected && (
                  <Icon name='check' size={11} color={colors.solid_color_primary} />
                )}
                <Text
                  size='s'
                  weight={isSelected ? 'bold' : 'medium'}
                  style={isSelected ? styles.chipLabelOnSelected : null}
                >
                  {opt.label || String(opt.value)}
                </Text>
              </Pressable>
            );
          })}
        </View>
        {(maxItems != null || selected.length > 0) && (
          <View style={styles.footerRow}>
            <Text size='xs' color='tertiary'>
              {selected.length}{maxItems != null ? ` / ${maxItems}` : ''} selected
            </Text>
            {selected.length > 0 && !disabled && (
              <Pressable onPress={() => onChange?.([])}>
                <Text size='xs' weight='bold' color='secondary'>Clear all</Text>
              </Pressable>
            )}
          </View>
        )}
      </View>
    );
  }

  // -- Free-form: tag-input chips + text entry ------------------------------
  return (
    <View>
      <View style={styles.wrap}>
        {selected.map((v) => (
          <View key={v} style={[styles.chip, styles.chipSelected]}>
            <Text size='s' weight='bold' style={styles.chipLabelOnSelected}>{v}</Text>
            {!disabled && (
              <Pressable onPress={() => removeChip(v)} hitSlop={6}>
                <Icon name='xmark' size={10} color={colors.solid_color_primary} />
              </Pressable>
            )}
          </View>
        ))}
        <TextInput
          value={draft}
          onChangeText={(t) => {
            // Allow paste of "a, b, c" — split on commas immediately.
            if (t.includes(',')) {
              const parts = t.split(',').map(s => s.trim()).filter(Boolean);
              const next = [...selected];
              for (const p of parts) {
                if (next.includes(p)) continue;
                if (maxItems != null && next.length >= maxItems) break;
                next.push(p);
              }
              onChange?.(next);
              setDraft('');
              return;
            }
            setDraft(t);
          }}
          onSubmitEditing={commitDraft}
          onBlur={commitDraft}
          onKeyPress={(e) => {
            if (Platform.OS === 'web' && e.nativeEvent?.key === 'Backspace' && draft === '' && selected.length) {
              e.preventDefault?.();
              removeChip(selected[selected.length - 1]);
            }
          }}
          editable={!disabled && !isAtCap}
          placeholder={selected.length ? '' : (placeholder || 'Type and press enter')}
          placeholderTextColor={colors.color_input_placeholder}
          style={styles.addInput}
          returnKeyType='done'
          blurOnSubmit={false}
        />
      </View>
      {(maxItems != null || selected.length > 0) && (
        <View style={styles.footerRow}>
          <Text size='xs' color='tertiary'>
            {selected.length}{maxItems != null ? ` / ${maxItems}` : ''} added · press enter to add
          </Text>
          {selected.length > 0 && !disabled && (
            <Pressable onPress={() => onChange?.([])}>
              <Text size='xs' weight='bold' color='secondary'>Clear all</Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Numeric helpers shared by NumberControl + SliderControl.
// ---------------------------------------------------------------------------

// Returns true when `text` is a partial-but-valid number-in-progress: empty,
// just a sign, just a decimal point, or any prefix that would parse cleanly
// (e.g. "12.", "-0.5"). We use this in onChange to reject obviously-invalid
// keystrokes so users never see "abc" land in a number field.
function isValidNumericInput(text, { allowDecimal = true, allowNegative = true } = {}) {
  if (text === '' || text === '-') return text !== '-' || allowNegative;
  if (text === '.') return allowDecimal;
  const pattern = allowDecimal
    ? (allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/)
    : (allowNegative ? /^-?\d*$/ : /^\d*$/);
  return pattern.test(text);
}

function clamp(n, min, max) {
  if (Number.isFinite(min) && n < min) return min;
  if (Number.isFinite(max) && n > max) return max;
  return n;
}

// ---------------------------------------------------------------------------
// NumberControl — strict numeric text input. Rejects non-numeric keystrokes,
// shows a clamp hint when the typed value is outside [min,max], and (on blur)
// quietly clamps the committed value so the form state always satisfies the
// declared range.
// ---------------------------------------------------------------------------
function NumberControl({ value, onChange, disabled, placeholder, min, max, step, error }) {
  const { colors } = useTheme();
  const allowDecimal = step == null || !Number.isInteger(step);
  const allowNegative = !Number.isFinite(min) || min < 0;

  const display = value == null ? '' : String(value);
  const parsed = display === '' || display === '-' || display === '.' ? null : Number(display);
  const outOfRange = parsed != null && Number.isFinite(parsed) && (
    (Number.isFinite(min) && parsed < min) || (Number.isFinite(max) && parsed > max)
  );

  const styles = useMemo(() => StyleSheet.create({
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: error || outOfRange ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      paddingLeft: 12,
      paddingRight: 4,
    },
    input: {
      flex: 1,
      paddingVertical: 10,
      color: colors.text_color_primary,
      fontSize: 15,
      fontVariant: ['tabular-nums'],
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    stepperCol: {
      borderLeftWidth: 1,
      borderLeftColor: colors.border_color_secondary,
      paddingLeft: 4,
    },
    stepperBtn: {
      width: 28, height: 18,
      alignItems: 'center', justifyContent: 'center',
      borderRadius: 4,
    },
    hint: { marginTop: 4 },
  }), [colors, error, outOfRange]);

  const stepBy = useCallback((delta) => {
    const cur = Number.isFinite(parsed) ? parsed : (Number.isFinite(min) ? min : 0);
    const stepSize = step || 1;
    const next = clamp(cur + delta * stepSize, min, max);
    onChange?.(allowDecimal ? Number(next.toFixed(6)) : Math.round(next));
  }, [allowDecimal, max, min, onChange, parsed, step]);

  return (
    <View>
      <View style={styles.field}>
        <TextInput
          value={display}
          onChangeText={(text) => {
            if (!isValidNumericInput(text, { allowDecimal, allowNegative })) return;
            if (text === '' || text === '-' || text === '.') {
              onChange?.(text);
              return;
            }
            const n = Number(text);
            onChange?.(Number.isNaN(n) ? text : n);
          }}
          onBlur={() => {
            if (!Number.isFinite(parsed)) return;
            const clamped = clamp(parsed, min, max);
            if (clamped !== parsed) onChange?.(clamped);
          }}
          editable={!disabled}
          placeholder={placeholder}
          placeholderTextColor={colors.color_input_placeholder}
          keyboardType={allowDecimal ? 'decimal-pad' : 'numeric'}
          inputMode={allowDecimal ? 'decimal' : 'numeric'}
          style={styles.input}
        />
        <View style={styles.stepperCol}>
          <Pressable onPress={() => stepBy(1)} disabled={disabled} style={styles.stepperBtn} hitSlop={4}>
            <Icon name='chevron-up' size={9} color={colors.text_color_secondary} />
          </Pressable>
          <Pressable onPress={() => stepBy(-1)} disabled={disabled} style={styles.stepperBtn} hitSlop={4}>
            <Icon name='chevron-down' size={9} color={colors.text_color_secondary} />
          </Pressable>
        </View>
      </View>

      {(Number.isFinite(min) || Number.isFinite(max)) && (
        <Text size='xs' color={outOfRange ? 'danger' : 'tertiary'} style={styles.hint}>
          {outOfRange
            ? `Must be between ${Number.isFinite(min) ? min : '−∞'} and ${Number.isFinite(max) ? max : '∞'}`
            : `Range: ${Number.isFinite(min) ? min : '−∞'} – ${Number.isFinite(max) ? max : '∞'}${step ? ` · step ${step}` : ''}`}
        </Text>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// UrlControl — URL text input with live validity feedback.
//   ✓ Empty: neutral (no icon)
//   ✓ Valid http(s) URL: green check icon
//   ✗ Looks-like-url-but-invalid: amber warning + helper text
// We deliberately do NOT block typing — the user might be mid-typing a URL.
// ---------------------------------------------------------------------------
function UrlControl({ value, onChange, disabled, placeholder, maxLength, error }) {
  const { colors } = useTheme();

  const validity = useMemo(() => {
    const v = (value || '').trim();
    if (!v) return 'empty';
    try {
      const u = new URL(v);
      return (u.protocol === 'http:' || u.protocol === 'https:') ? 'valid' : 'wrongProtocol';
    } catch {
      // Allow protocol-relative-ish typing without a warning until they reach
      // something that looks like a URL but doesn't parse.
      return /^(https?:|www\.|[\w-]+\.[\w]{2,})/i.test(v) ? 'invalid' : 'pending';
    }
  }, [value]);

  const isError = validity === 'invalid' || validity === 'wrongProtocol' || !!error;

  const styles = useMemo(() => StyleSheet.create({
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: isError ? colors.color_destructive : colors.border_color_secondary,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      paddingHorizontal: 12,
    },
    input: {
      flex: 1,
      paddingVertical: 10,
      color: colors.text_color_primary,
      fontSize: 15,
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    leadingIcon: { opacity: 0.7 },
    hint: { marginTop: 4 },
  }), [colors, isError]);

  const trailingIcon = (
    validity === 'valid' ? { name: 'circle-check', color: '#22c55e' } :
    validity === 'invalid' ? { name: 'triangle-exclamation', color: colors.color_destructive } :
    validity === 'wrongProtocol' ? { name: 'triangle-exclamation', color: '#eab308' } :
    null
  );

  return (
    <View>
      <View style={styles.field}>
        <Icon name='link' size={13} color={colors.text_color_secondary} style={styles.leadingIcon} />
        <TextInput
          value={value || ''}
          onChangeText={onChange}
          editable={!disabled}
          placeholder={placeholder}
          placeholderTextColor={colors.color_input_placeholder}
          keyboardType='url'
          autoCapitalize='none'
          autoCorrect={false}
          autoComplete='url'
          maxLength={maxLength}
          style={styles.input}
        />
        {trailingIcon && (
          <Icon name={trailingIcon.name} size={14} color={trailingIcon.color} />
        )}
      </View>
      {validity === 'invalid' && (
        <Text size='xs' color='danger' style={styles.hint}>
          That doesn't look like a valid URL.
        </Text>
      )}
      {validity === 'wrongProtocol' && (
        <Text size='xs' style={[styles.hint, { color: '#eab308' }]}>
          URL must start with http:// or https://
        </Text>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// SliderControl — gradient-track range slider with an inline editable value
// box that clamps to [min,max]. The number box doubles as the visual readout
// AND the precise-input affordance for users who don't want to drag.
// ---------------------------------------------------------------------------
function SliderControl({ value, onChange, disabled, min, max, step, error }) {
  const { colors } = useTheme();

  const numericValue = useMemo(() => {
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(n)) return clamp(n, min, max);
    return min;
  }, [value, min, max]);

  const [editing, setEditing] = useState(String(numericValue));
  useEffect(() => { setEditing(String(numericValue)); }, [numericValue]);

  const allowDecimal = !Number.isInteger(step);
  const allowNegative = min < 0;
  const range = Math.max(0.0001, max - min);
  const fillPct = ((numericValue - min) / range) * 100;

  const styles = useMemo(() => StyleSheet.create({
    row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    // Track wrapper holds three absolutely-positioned layers, all explicitly
    // pinned to the vertical centre. Without explicit `top` values the layers
    // collapse to the parent's top edge and the filled segment effectively
    // disappears behind the unfilled track.
    trackWrap: { flex: 1, minWidth: 0, height: 28 },
    trackBg: {
      position: 'absolute',
      left: 0, right: 0,
      top: '50%',
      marginTop: -5,
      height: 10,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_primary,
    },
    trackFill: {
      position: 'absolute',
      left: 0,
      top: '50%',
      marginTop: -5,
      height: 10,
      borderRadius: 999,
      overflow: 'hidden',
    },
    fillGradient: { flex: 1, borderRadius: 999 },
    thumb: {
      position: 'absolute',
      top: '50%',
      marginTop: -10,
      width: 20, height: 20,
      borderRadius: 999,
      backgroundColor: '#ffffff',
      borderWidth: 2,
      borderColor: '#4338ca',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 6px rgba(67,56,202,0.35)',
      }),
    },
    valueBox: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: error ? colors.color_destructive : colors.border_color_primary,
      borderRadius: 8,
      backgroundColor: colors.solid_color_primary,
      paddingLeft: 8,
      paddingRight: 4,
      minWidth: 78,
    },
    valueInput: {
      flex: 1,
      paddingVertical: 7,
      color: colors.text_color_primary,
      fontSize: 14,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
      textAlign: 'center',
      ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    },
    bounds: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  }), [colors, error]);

  const commit = useCallback((next) => {
    const clamped = clamp(next, min, max);
    onChange?.(allowDecimal ? Number(clamped.toFixed(6)) : Math.round(clamped));
  }, [allowDecimal, max, min, onChange]);

  return (
    <View>
      <View style={styles.row}>
        <View style={styles.trackWrap}>
          <View style={styles.trackBg} />
          <View style={[styles.trackFill, { width: `${fillPct}%` }]}>
            <LinearGradient
              colors={['#7c3aed', '#4338ca']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.fillGradient}
            />
          </View>
          {Platform.OS === 'web' ? (
            // eslint-disable-next-line react/forbid-elements
            <input
              type='range'
              min={min}
              max={max}
              step={step}
              value={numericValue}
              disabled={disabled}
              onChange={(e) => commit(Number(e.target.value))}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                margin: 0,
                opacity: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                WebkitAppearance: 'none',
              }}
            />
          ) : null}
          {Platform.OS === 'web' && (
            <View
              pointerEvents='none'
              style={[styles.thumb, { left: `calc(${fillPct}% - 10px)` }]}
            />
          )}
        </View>

        <View style={styles.valueBox}>
          <TextInput
            value={editing}
            onChangeText={(text) => {
              if (!isValidNumericInput(text, { allowDecimal, allowNegative })) return;
              setEditing(text);
              if (text === '' || text === '-' || text === '.') return;
              const n = Number(text);
              if (Number.isFinite(n)) commit(n);
            }}
            onBlur={() => {
              const n = Number(editing);
              if (!Number.isFinite(n)) setEditing(String(numericValue));
              else commit(n);
            }}
            editable={!disabled}
            keyboardType={allowDecimal ? 'decimal-pad' : 'numeric'}
            inputMode={allowDecimal ? 'decimal' : 'numeric'}
            style={styles.valueInput}
          />
        </View>
      </View>

      <View style={styles.bounds}>
        <Text size='xs' color='tertiary'>{min}</Text>
        <Text size='xs' color='tertiary'>{max}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// FileFieldControl — single-file uploader.
//
// Wraps the shared Dropzone + FilePreviewTile so single-file inputs share the
// same dashed drag-and-drop experience as multi-file ones, and rich previews
// for images / videos / audio. Emits the file ID string on success and stashes
// the full metadata in local state so the preview can render without an extra
// fetch when the form re-mounts.
// ---------------------------------------------------------------------------
function FileFieldControl({
  value,
  onChange,
  disabled,
  accept,
  label,
  limitsHint,
  maxSizeBytes,
  maxDurationSeconds,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError]  = useState(null);
  const [meta, setMeta] = useState(() => (
    value && typeof value === 'object' && (value.name || value.cdnPath) ? value : null
  ));

  const fileId = (typeof value === 'string')
    ? value
    : (value?.id || value?._id || null);

  // Placeholder = preset previews (e.g. unsplash sample) that have a cdnPath
  // but no real backend file id. We still render a preview tile so the user
  // can see what the example looked like, with a "SAMPLE" badge.
  const isSamplePreview = !!(value && typeof value === 'object' && !fileId && (value._placeholder || value.cdnPath));

  // If the form was reset externally, drop our cached preview metadata.
  useEffect(() => {
    if (value && typeof value === 'object' && (value.name || value.cdnPath)) {
      setMeta(value);
    } else if (!fileId) {
      setMeta(null);
    }
  }, [fileId, value]);

  const acceptsImages = (accept || '').includes('image');
  const acceptsAudio  = (accept || '').includes('audio');
  const acceptsVideo  = (accept || '').includes('video');
  const dropIcon =
    acceptsImages ? 'image'
    : acceptsAudio ? 'waveform'
    : acceptsVideo ? 'film'
    : 'cloud-arrow-up';
  const dropPrimary = label || (
    acceptsImages ? 'Drag and drop an image here'
    : acceptsAudio ? 'Drag and drop audio here'
    : acceptsVideo ? 'Drag and drop a video here'
    : 'Drag and drop a file here'
  );

  const handleUpload = useCallback(async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    setUploadError(null);
    const probedDuration = await probeMediaDurationSeconds(file);
    const limitError = describeFileLimitViolation(
      { size: file.size, duration: probedDuration },
      { maxSizeBytes, maxDurationSeconds },
    );
    if (limitError) {
      setUploadError(limitError);
      return;
    }
    setIsUploading(true);
    try {
      const { file: uploaded } = await filesApi.uploadFile({ file });
      if (!uploaded?.id) throw new Error('Upload failed');
      const next = {
        id: uploaded.id,
        name: uploaded.name || uploaded.originalName || file.name,
        mimeType: uploaded.mimeType || file.type,
        sizeInBytes: uploaded.sizeInBytes ?? uploaded.sizeBytes ?? file.size,
        cdnPath: uploaded.cdnPath || null,
        thumbnailPath: uploaded.thumbnailPath || null,
        fileType: uploaded.fileType,
        metadata: uploaded.metadata || null,
      };
      setMeta(next);
      onChange?.(next.id);
    } catch (e) {
      setUploadError(e?.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const handleLibrary = useCallback((picked) => {
    const saved = picked?.[0];
    if (!saved?.id) return;
    const limitError = describeFileLimitViolation(saved, { maxSizeBytes, maxDurationSeconds });
    if (limitError) {
      setUploadError(limitError);
      return;
    }
    setUploadError(null);
    const next = {
      id: saved.id,
      name: saved.name || saved.originalName,
      mimeType: saved.mimeType,
      sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes,
      cdnPath: saved.cdnPath || null,
      thumbnailPath: saved.thumbnailPath || null,
      fileType: saved.fileType,
      metadata: saved.metadata || null,
    };
    setMeta(next);
    onChange?.(next.id);
  }, [onChange, maxSizeBytes, maxDurationSeconds]);

  const handleClear = useCallback(() => {
    setMeta(null);
    onChange?.(null);
  }, [onChange]);

  if ((fileId || isSamplePreview) && meta) {
    return (
      <View style={{ gap: 10, alignItems: 'flex-start' }}>
        <FilePreviewTile
          file={meta}
          isSample={isSamplePreview}
          onRemove={disabled ? null : handleClear}
          contentFit='contain'
        />
        {!!uploadError && <Text color='danger' size='s'>{uploadError}</Text>}
      </View>
    );
  }

  return (
    <View style={{ gap: 6 }}>
      <Dropzone
        accept={accept}
        disabled={disabled}
        isUploading={isUploading}
        icon={dropIcon}
        primaryLabel={dropPrimary}
        secondaryLabel={limitsHint || (accept && accept !== '*/*' ? `Accepted: ${accept}` : null)}
        onFiles={handleUpload}
        onLibrary={handleLibrary}
      />
      {!!uploadError && <Text color='danger' size='s'>{uploadError}</Text>}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Date / time helpers shared by the date/time/datetime/daterange controls.
// ---------------------------------------------------------------------------
const pad2 = (n) => String(n).padStart(2, '0');

// Date-only string in `YYYY-MM-DD` (matches <input type="date"> value format).
function fmtDate(d) {
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function parseDate(str) {
  if (!str || typeof str !== 'string') return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Time-only string in `HH:MM` (24-hour, matches <input type="time"> value format).
function fmtTime(d) {
  if (!d || isNaN(d.getTime())) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function parseTime(str) {
  if (!str || typeof str !== 'string') return null;
  const [h, m] = str.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

// `<input type="datetime-local">` uses `YYYY-MM-DDTHH:mm` with no timezone.
function fmtDateTimeLocal(d) {
  if (!d || isNaN(d.getTime())) return '';
  return `${fmtDate(d)}T${fmtTime(d)}`;
}
function parseDateTimeLocal(str) {
  if (!str || typeof str !== 'string') return null;
  const [datePart, timePart] = str.split('T');
  const date = parseDate(datePart);
  if (!date) return null;
  if (timePart) {
    const [h, m] = timePart.split(':').map(Number);
    if (!Number.isNaN(h)) date.setHours(h);
    if (!Number.isNaN(m)) date.setMinutes(m);
  }
  return date;
}

// ---------------------------------------------------------------------------
// Shared style factory for the input "shell" used by color/date/time/datetime.
// All of them render a bordered surface that matches the rest of the form;
// the actual input lives inside it (native HTML on web, Pressable + modal-like
// picker on native).
// ---------------------------------------------------------------------------
function useFieldShellStyles() {
  const { colors } = useTheme();
  return useMemo(() => StyleSheet.create({
    shell: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      minHeight: 44,
    },
    shellDisabled: { opacity: 0.6 },
    valueText: { flex: 1, color: colors.text_color_primary },
    placeholderText: { flex: 1, color: colors.color_input_placeholder },
    swatch: {
      width: 28,
      height: 28,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    rowGap: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    webBareInput: {
      flex: 1,
      width: '100%',
      backgroundColor: 'transparent',
      color: colors.text_color_primary,
      border: 'none',
      outline: 'none',
      fontSize: 15,
      // Native HTML inputs don't inherit RN's text styles, so set the app font
      // explicitly. Otherwise web ships browser defaults (Chrome's date/time
      // pickers default to a serif/proportional font that clashes with the UI).
      fontFamily: APP_FONT_FAMILY,
      colorScheme: 'inherit',
    },
    iconBtn: {
      paddingHorizontal: 6,
      paddingVertical: 4,
    },
  }), [colors]);
}

// ---------------------------------------------------------------------------
// ColorControl — hex color picker.
// Web: native <input type="color"> + a paired hex text field for typing.
// Native: hex text field with a live preview swatch (no native dep needed).
// Emits a hex string like `#ff8800`.
// ---------------------------------------------------------------------------
function ColorControl({ value, onChange, disabled, placeholder }) {
  const { colors } = useTheme();
  const styles = useFieldShellStyles();
  const hex = useMemo(() => {
    const v = typeof value === 'string' ? value.trim() : '';
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      const r = v[1], g = v[2], b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return '#000000';
  }, [value]);

  return (
    <View style={[styles.shell, disabled && styles.shellDisabled]}>
      {Platform.OS === 'web' ? (
        // eslint-disable-next-line react/forbid-elements
        <input
          type='color'
          value={hex}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          style={{
            width: 36,
            height: 36,
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
      ) : (
        <View style={[styles.swatch, { backgroundColor: hex }]} />
      )}
      <TextInput
        value={value || ''}
        onChangeText={onChange}
        editable={!disabled}
        placeholder={placeholder}
        placeholderTextColor={colors.color_input_placeholder}
        autoCapitalize='none'
        autoCorrect={false}
        style={[styles.webBareInput, { fontVariant: ['tabular-nums'] }]}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// DateControl — single date in `YYYY-MM-DD`.
// Web uses <input type="date"> (free, full-featured calendar UI in browsers).
// Native uses @react-native-community/datetimepicker, opened on press.
// ---------------------------------------------------------------------------
function DateControl({ value, onChange, disabled, placeholder }) {
  const { colors } = useTheme();
  const styles = useFieldShellStyles();
  const [showPicker, setShowPicker] = useState(false);

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.shell, disabled && styles.shellDisabled]}>
        <Icon name='calendar-day' size={14} color={colors.text_color_secondary} />
        {/* eslint-disable-next-line react/forbid-elements */}
        <input
          type='date'
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value || null)}
          style={styles.webBareInput}
        />
      </View>
    );
  }

  return (
    <>
      <Pressable
        disabled={disabled}
        onPress={() => setShowPicker(true)}
        style={[styles.shell, disabled && styles.shellDisabled]}
      >
        <Icon name='calendar-day' size={14} color={colors.text_color_secondary} />
        <Text style={value ? styles.valueText : styles.placeholderText}>
          {value || placeholder}
        </Text>
        {value ? (
          <Pressable onPress={() => onChange?.(null)} style={styles.iconBtn}>
            <Icon name='xmark' size={12} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </Pressable>
      {showPicker && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={parseDate(value) || new Date()}
          mode='date'
          display={Platform.OS === 'ios' ? 'inline' : 'default'}
          onChange={(_, picked) => {
            setShowPicker(false);
            if (picked) onChange?.(fmtDate(picked));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TimeControl — single time-of-day in `HH:MM` (24h).
// ---------------------------------------------------------------------------
function TimeControl({ value, onChange, disabled, placeholder }) {
  const { colors } = useTheme();
  const styles = useFieldShellStyles();
  const [showPicker, setShowPicker] = useState(false);

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.shell, disabled && styles.shellDisabled]}>
        <Icon name='clock' size={14} color={colors.text_color_secondary} />
        {/* eslint-disable-next-line react/forbid-elements */}
        <input
          type='time'
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value || null)}
          style={styles.webBareInput}
        />
      </View>
    );
  }

  return (
    <>
      <Pressable
        disabled={disabled}
        onPress={() => setShowPicker(true)}
        style={[styles.shell, disabled && styles.shellDisabled]}
      >
        <Icon name='clock' size={14} color={colors.text_color_secondary} />
        <Text style={value ? styles.valueText : styles.placeholderText}>
          {value || placeholder}
        </Text>
        {value ? (
          <Pressable onPress={() => onChange?.(null)} style={styles.iconBtn}>
            <Icon name='xmark' size={12} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </Pressable>
      {showPicker && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={parseTime(value) || new Date()}
          mode='time'
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={(_, picked) => {
            setShowPicker(false);
            if (picked) onChange?.(fmtTime(picked));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DateTimeControl — combined date + time in `YYYY-MM-DDTHH:MM`.
// On web this is one native input. On native we open a date picker, then a
// time picker, and combine the result.
// ---------------------------------------------------------------------------
function DateTimeControl({ value, onChange, disabled, placeholder }) {
  const { colors } = useTheme();
  const styles = useFieldShellStyles();
  const [stage, setStage] = useState(null); // 'date' | 'time' | null
  const draftRef = useRef(null);

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.shell, disabled && styles.shellDisabled]}>
        <Icon name='calendar-clock' size={14} color={colors.text_color_secondary} />
        {/* eslint-disable-next-line react/forbid-elements */}
        <input
          type='datetime-local'
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value || null)}
          style={styles.webBareInput}
        />
      </View>
    );
  }

  const display = value ? value.replace('T', ' ') : '';
  return (
    <>
      <Pressable
        disabled={disabled}
        onPress={() => {
          draftRef.current = parseDateTimeLocal(value) || new Date();
          setStage('date');
        }}
        style={[styles.shell, disabled && styles.shellDisabled]}
      >
        <Icon name='calendar-clock' size={14} color={colors.text_color_secondary} />
        <Text style={value ? styles.valueText : styles.placeholderText}>
          {display || placeholder}
        </Text>
        {value ? (
          <Pressable onPress={() => onChange?.(null)} style={styles.iconBtn}>
            <Icon name='xmark' size={12} color={colors.text_color_secondary} />
          </Pressable>
        ) : null}
      </Pressable>

      {stage === 'date' && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={draftRef.current || new Date()}
          mode='date'
          display={Platform.OS === 'ios' ? 'inline' : 'default'}
          onChange={(_, picked) => {
            if (!picked) { setStage(null); return; }
            draftRef.current = new Date(
              picked.getFullYear(), picked.getMonth(), picked.getDate(),
              draftRef.current?.getHours() ?? 0, draftRef.current?.getMinutes() ?? 0,
            );
            setStage('time');
          }}
        />
      )}
      {stage === 'time' && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={draftRef.current || new Date()}
          mode='time'
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={(_, picked) => {
            setStage(null);
            if (!picked || !draftRef.current) return;
            const merged = new Date(draftRef.current);
            merged.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
            onChange?.(fmtDateTimeLocal(merged));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DateRangeFieldControl — start + end dates as `[startISO, endISO]`.
// Emits an array of 2 strings (or `null` for either side). Backend already
// validates arrays via the standard `array` validation block.
// ---------------------------------------------------------------------------
function DateRangeFieldControl({ value, onChange, disabled }) {
  const { colors } = useTheme();
  const styles = useMemo(() => StyleSheet.create({
    row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    half: { flex: 1, minWidth: 0 },
    label: { color: colors.text_color_secondary, width: 36 },
  }), [colors]);

  const [start, end] = Array.isArray(value) ? value : [null, null];

  const propagate = useCallback((s, e) => {
    if (!s && !e) onChange?.(null);
    else onChange?.([s || null, e || null]);
  }, [onChange]);

  return (
    <View style={{ gap: 8 }}>
      <View style={styles.row}>
        <Text size='s' style={styles.label}>Start</Text>
        <View style={styles.half}>
          <DateControl
            value={start}
            onChange={(v) => propagate(v, end)}
            disabled={disabled}
            placeholder='YYYY-MM-DD'
          />
        </View>
      </View>
      <View style={styles.row}>
        <Text size='s' style={styles.label}>End</Text>
        <View style={styles.half}>
          <DateControl
            value={end}
            onChange={(v) => propagate(start, v)}
            disabled={disabled}
            placeholder='YYYY-MM-DD'
          />
        </View>
      </View>
    </View>
  );
}

export default React.memo(ToolFormField);
